import { StakingContract, useUser } from '@trustpad/launchpad';
import { StakingContractV2 } from '@trustpad/launchpad/dist/staking/hooks/useStakingContract';
import { StakingState } from '@trustpad/launchpad/dist/staking/hooks/useStakingV2';

import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { BLOCKS_PER_DAY, ZERO_BN } from '~config/constants';
import {
  call,
  denormalizeDecimals,
  isValidValue,
  normalizeDecimals,
  toEth,
  toWeiBN,
} from '~utils';

export function useStakingAdmin({
  contract,
  apr,
  state,
  rewardDecimals,
  refresh,
}: {
  contract: StakingContract | StakingContractV2;
  apr: number;
  state: StakingState;
  rewardDecimals: number;
  refresh?: VoidFunction;
}) {
  const { account } = useUser();
  const [reward, setReward] = useState('0');

  const [treasury, setTreasury] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setReward(toEth(normalizeDecimals(state.rewardPerBlock, rewardDecimals)));
  }, [state.rewardPerBlock, rewardDecimals]);

  const rewardBN = isValidValue(reward)
    ? denormalizeDecimals(toWeiBN(reward), rewardDecimals)
    : ZERO_BN;
  const rewardPerDay = BLOCKS_PER_DAY * parseFloat(reward);
  const newApr = state.rewardPerBlock.gtn(0)
    ? rewardBN.muln(apr).div(state.rewardPerBlock).toNumber()
    : apr;

  useEffect(() => {
    if (!contract) return;
    call(contract, 'treasury')
      .then(setTreasury)
      .catch(() => {});
  }, [contract]);

  const saveReward = () => {
    setIsLoading(true);
    return contract.methods
      .setRewardPerBlock(rewardBN)
      .send({ from: account })
      .then(() => {
        toast.success('Rate updated');
        refresh?.();
      })
      .catch((e) => {
        toast.error(`Failed to update rate: ${e.message}`);
      })
      .finally(() => setIsLoading(false));
  };

  return {
    reward,
    setReward,
    rewardPerDay,
    treasury,
    isLoading,
    newApr,
    saveReward,
  };
}
